// The RFQ Center: every RFQ by who is waiting on whom, in four views over the
// same rows (Inbox, RFQs, Parts, Suppliers). Opening a row keeps the list and
// opens that supplier's conversation beside it.
// apps/smart-catalog/frontend/src/components/buyer-agent/rfq-center/RfqCenter.tsx
//
// The view, the gauge filter and the open conversation live in the URL, so a
// link, a refresh and Back all return to the same place.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useParams, useSearchParams } from 'react-router-dom';
import { Box, Button, Paper, Skeleton, Stack, Tab, Tabs, Typography, alpha, useTheme } from '@mui/material';
import {
  AllInbox as AllIcon,
  CheckCircleOutline as QuotedIcon,
  EditNote as DraftIcon,
  HourglassEmpty as AwaitingIcon,
  PersonOutline as YouIcon,
  Refresh as RefreshIcon,
  WarningAmber as StaleIcon,
} from '@mui/icons-material';
import { useBreadcrumbUpdater } from '@ciea.ai/smart-infra-navigation';
import bomV2Service from '../../../services/bomV2Service';
import rfqCenterService, { CenterCounts, CenterReferral, CenterRow } from '../../../services/rfqCenterService';
import UnifiedSearchBar, { BomOption, UnifiedSearchValue } from '../../search/UnifiedSearchBar';
import RfqMetricGauge from '../RfqMetricGauge';
import ConversationPanel from './ConversationPanel';
import GroupPanel from './GroupPanel';
import ReferralPanel from './ReferralPanel';
import { CenterFilter, CenterGroup, CenterTab, TAB_LABEL, askedLine, groupKey, groupRows, inboxRows, matchesFilter, matchesSearch, siblingsOf, stateColors, activityLabel } from './centerView';

const TABS: CenterTab[] = ['inbox', 'rfqs', 'parts', 'suppliers'];
const FILTERS: CenterFilter[] = ['all', 'you', 'wait', 'stale', 'quoted', 'draft'];
const GAUGES: Array<{ id: CenterFilter; label: string; color: string; Icon: React.ComponentType<{ sx?: object }>; sublabel?: string }> = [
  { id: 'all', label: 'All', color: '#475569', Icon: AllIcon },
  { id: 'you', label: 'Needs you', color: '#1D4ED8', Icon: YouIcon },
  { id: 'wait', label: 'Awaiting', color: '#d97706', Icon: AwaitingIcon },
  { id: 'stale', label: 'Stale', color: '#dc2626', Icon: StaleIcon, sublabel: '≥7d' },
  { id: 'quoted', label: 'Quoted', color: '#0f766e', Icon: QuotedIcon },
  { id: 'draft', label: 'Drafts', color: '#6B7280', Icon: DraftIcon },
];
const LABEL_SX = { fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700, color: 'text.disabled' } as const;
const EMPTY_COUNTS: CenterCounts = { all: 0, you: 0, wait: 0, stale: 0, quoted: 0, draft: 0 };

const StatusChip: React.FC<{ row: CenterRow }> = ({ row }) => {
  const c = stateColors(row);
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', height: 24, px: 1.25, borderRadius: 1, border: `1px solid ${c.color}`, color: c.color, bgcolor: c.bg, fontSize: '0.72rem', fontWeight: 600, whiteSpace: 'nowrap' }}>
      {row.chip}
      {row.pilotRequestId && (
        <Box component="span" title="Written and followed by the AI autopilot" sx={{ ml: 0.75, pl: 0.75, borderLeft: `1px solid ${c.color}`, fontWeight: 700 }}>
          Autopilot
        </Box>
      )}
    </Box>
  );
};

const waitingText = (row: CenterRow): string => (row.waitingOn.days !== undefined ? `${row.waitingOn.days} ${row.waitingOn.days === 1 ? 'day' : 'days'}` : '');

const RfqCenter: React.FC = () => {
  useBreadcrumbUpdater([{ path: '/rfq', breadcrumb: 'RFQ Center' }]);
  const theme = useTheme();
  const [params, setParams] = useSearchParams();
  const [rows, setRows] = useState<CenterRow[]>([]);
  // Sellers we did not write to that wrote about our RFQs: the buyer says who they are.
  const [referrals, setReferrals] = useState<CenterReferral[]>([]);
  const [counts, setCounts] = useState<CenterCounts>(EMPTY_COUNTS);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  // A link from another screen (a BOM line, an inbox card) lands on the conversation itself, full width.
  const [expanded, setExpanded] = useState(params.get('full') === '1');
  const [search, setSearch] = useState<UnifiedSearchValue>({ identifier: params.get('q') ?? '', bomId: params.get('bomId'), text: params.get('text') ?? '' });

  const tab: CenterTab = TABS.includes(params.get('view') as CenterTab) ? (params.get('view') as CenterTab) : 'inbox';
  const filter: CenterFilter = FILTERS.includes(params.get('f') as CenterFilter) ? (params.get('f') as CenterFilter) : 'all';
  // `rfqId` is how older links name the RFQ to open.
  const openId = params.get('open') ?? params.get('rfqId');
  const partIdFilter = params.get('partId');
  const supplierIdFilter = params.get('supplierId');
  const groupId = params.get('group');
  const referralId = params.get('referral');

  const setParam = useCallback(
    (changes: Record<string, string | null>) => {
      const next = new URLSearchParams(params);
      for (const [key, value] of Object.entries(changes)) {
        if (value) next.set(key, value);
        else next.delete(key);
      }
      setParams(next, { replace: true });
    },
    [params, setParams],
  );

  const load = useCallback(async () => {
    try {
      const data = await rfqCenterService.getRows(search.bomId);
      setRows(data?.rows ?? []);
      setReferrals(data?.referrals ?? []);
      setCounts(data?.counts ?? EMPTY_COUNTS);
      setFailed(false);
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, [search.bomId]);

  useEffect(() => {
    void load();
  }, [load]);

  const fetchBoms = useCallback(async (query: string): Promise<BomOption[]> => {
    try {
      const res = await bomV2Service.listBoms({ query, limit: 25 });
      return (res.items ?? []).map((b) => ({ bomId: b.bomId, bomName: b.bomName || b.bomId }));
    } catch {
      return [];
    }
  }, []);

  const visible = useMemo(
    () =>
      rows.filter(
        (r) =>
          matchesFilter(r, filter) &&
          matchesSearch(r, search.identifier, search.text) &&
          (!partIdFilter || r.lines.some((l) => l.partId === partIdFilter)) &&
          (!supplierIdFilter || r.supplier.id === supplierIdFilter),
      ),
    [rows, filter, search.identifier, search.text, partIdFilter, supplierIdFilter],
  );
  const openRow = useMemo(() => (openId ? rows.find((r) => r.rfqId === openId) : undefined), [rows, openId]);
  const groups = useMemo(() => (tab === 'inbox' ? [] : groupRows(visible, tab)), [visible, tab]);
  // The group on screen: the one picked, or the one the open conversation belongs to.
  const openGroup = useMemo(() => {
    if (tab === 'inbox') return undefined;
    const key = openRow ? groupKey(openRow, tab) : groupId;
    return key ? groups.find((g) => g.key === key) : undefined;
  }, [tab, openRow, groupId, groups]);
  const openReferral = useMemo(() => (referralId ? referrals.find((r) => r.mailId === referralId) : undefined), [referrals, referralId]);
  const detail = !!openRow || !!openGroup || !!openReferral;
  const split = detail && !expanded;

  /** One RFQ, part or supplier is open full width: the page is about it, not about the list. */
  // Arriving on a link to one RFQ: hold its frame while the data loads, so the list never flashes first.
  const arriving = loading && expanded && !!openId;
  const focused = (detail && expanded) || arriving;
  const leaveFocus = () => {
    setExpanded(false);
    setParam({ open: null, group: null, rfqId: null, full: null });
  };

  const open = (rfqId: string) => setParam({ open: rfqId, referral: null });

  const rowCard = (row: CenterRow) => {
    const c = stateColors(row);
    const selected = row.rfqId === openId;
    return (
      <Paper
        key={row.rfqId}
        elevation={0}
        onClick={() => open(row.rfqId)}
        sx={{
          cursor: 'pointer',
          border: `1px solid ${theme.palette.divider}`,
          borderLeft: `4px solid ${c.rail}`,
          borderRadius: 2,
          px: 2.5,
          py: 1.75,
          display: 'grid',
          alignItems: 'center',
          gap: 2,
          gridTemplateColumns: split ? 'minmax(0, 1fr) auto' : { xs: '1fr', md: 'minmax(0, 1.4fr) 104px minmax(0, 2.4fr) minmax(0, 1.3fr) auto 84px' },
          outline: selected ? `2px solid ${theme.palette.primary.main}` : 'none',
          '&:hover': { boxShadow: `0 2px 8px ${alpha(c.rail, 0.12)}` },
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={LABEL_SX}>Supplier</Typography>
          <Typography variant="body2" fontWeight={700} noWrap>
            {row.supplier.name}
          </Typography>
          {!split && row.nowWith?.isNew && (
            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
              {row.nowWith.name}
            </Typography>
          )}
        </Box>
        {!split && (
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={LABEL_SX}>Created</Typography>
            <Typography variant="body2" noWrap sx={{ fontVariantNumeric: 'tabular-nums' }}>
              {createdDay(row.createdAt)}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', fontVariantNumeric: 'tabular-nums' }}>
              {createdTime(row.createdAt)}
            </Typography>
          </Box>
        )}
        {!split && (
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={LABEL_SX}>
              Latest{activityLabel(row) ? ` · ${activityLabel(row)}` : ''}
            </Typography>
            <Typography dir="auto" variant="body2" noWrap>
              {row.latest?.text ?? row.waitingOn.detail ?? (row.state === 'draft' ? 'Not sent' : 'No reply yet')}
            </Typography>
          </Box>
        )}
        {!split && (
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={LABEL_SX}>RFQ</Typography>
            <Typography variant="body2" fontWeight={700} noWrap>
              {row.rfqNumber.split('-').pop()}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
              {askedLine(row)}
            </Typography>
          </Box>
        )}
        <StatusChip row={row} />
        {!split && (
          <Box sx={{ textAlign: 'right' }}>
            <Typography sx={LABEL_SX}>Waiting</Typography>
            <Typography variant="body2" fontWeight={row.stale ? 700 : 500} sx={{ color: row.stale ? theme.palette.error.main : 'text.primary' }}>
              {waitingText(row)}
            </Typography>
          </Box>
        )}
      </Paper>
    );
  };

  /** One RFQ, part or supplier as a single clean row, the same card as an Inbox row. */
  const groupCard = (group: CenterGroup) => {
    const c = stateColors(group.lead);
    const selected = openGroup?.key === group.key;
    const progress = `${group.counts.asked} asked · ${group.counts.replied} replied · ${group.counts.quoted} quoted`;
    return (
      <Paper
        key={group.key}
        elevation={0}
        onClick={() => setParam({ group: group.key, open: null })}
        sx={{
          cursor: 'pointer',
          border: `1px solid ${theme.palette.divider}`,
          borderLeft: `4px solid ${c.rail}`,
          borderRadius: 2,
          px: 2.5,
          py: 1.75,
          display: 'grid',
          alignItems: 'center',
          gap: 2,
          gridTemplateColumns: split ? 'minmax(0, 1fr) auto' : { xs: '1fr', md: 'minmax(0, 1.6fr) minmax(0, 1.4fr) minmax(0, 1.8fr) auto 84px' },
          outline: selected ? `2px solid ${theme.palette.primary.main}` : 'none',
          '&:hover': { boxShadow: `0 2px 8px ${alpha(c.rail, 0.12)}` },
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={LABEL_SX}>{tab === 'suppliers' ? 'Supplier' : 'Part'}</Typography>
          <Typography variant="body2" fontWeight={700} noWrap>
            {group.title}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
            {split ? progress : group.subtitle}
          </Typography>
        </Box>
        {!split && (
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={LABEL_SX}>{tab === 'suppliers' ? 'RFQs' : 'Suppliers'}</Typography>
            <Typography variant="body2" fontWeight={600} noWrap>
              {progress}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
              {group.bestQuote ? `Best quote ${group.bestQuote}` : group.counts.drafts ? `${group.counts.drafts} ${group.counts.drafts === 1 ? 'draft' : 'drafts'} not sent` : 'No quote yet'}
            </Typography>
          </Box>
        )}
        {!split && (
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={LABEL_SX}>Latest</Typography>
            <Typography dir="auto" variant="body2" noWrap>
              {group.latest ?? (group.counts.asked ? 'No reply yet' : 'Not sent')}
            </Typography>
          </Box>
        )}
        <StatusChip row={group.lead} />
        {!split && (
          <Box sx={{ textAlign: 'right' }}>
            <Typography sx={LABEL_SX}>Waiting</Typography>
            <Typography variant="body2" fontWeight={group.lead.stale ? 700 : 500} sx={{ color: group.lead.stale ? theme.palette.error.main : 'text.primary' }}>
              {waitingText(group.lead)}
            </Typography>
          </Box>
        )}
      </Paper>
    );
  };

  /** A seller we did not write to, about one of our RFQs: first in what needs the buyer. */
  const referralCard = (ref: CenterReferral) => {
    const selected = ref.mailId === referralId;
    const rail = '#1D4ED8';
    return (
      <Paper
        key={`referral-${ref.mailId}`}
        elevation={0}
        onClick={() => setParam({ referral: ref.mailId, open: null, group: null })}
        sx={{
          cursor: 'pointer',
          border: `1px solid ${theme.palette.divider}`,
          borderLeft: `4px solid ${rail}`,
          borderRadius: 2,
          px: 2.5,
          py: 1.75,
          display: 'grid',
          alignItems: 'center',
          gap: 2,
          gridTemplateColumns: split ? 'minmax(0, 1fr) auto' : { xs: '1fr', md: 'minmax(0, 1.4fr) minmax(0, 3.8fr) auto 84px' },
          outline: selected ? `2px solid ${theme.palette.primary.main}` : 'none',
          '&:hover': { boxShadow: `0 2px 8px ${alpha(rail, 0.12)}` },
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={LABEL_SX}>Seller</Typography>
          <Typography variant="body2" fontWeight={700} noWrap>
            {ref.domain}
          </Typography>
          {split && (
            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
              Through {ref.referrerName ?? 'another seller'}
            </Typography>
          )}
        </Box>
        {!split && (
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={LABEL_SX}>Wrote about</Typography>
            <Typography variant="body2" noWrap>
              {ref.rfqNumber?.split('-').pop()} · {ref.item}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
              Got our RFQ through {ref.referrerName ?? 'another seller'}
              {ref.automatic?.length ? ' · Automatic reminders' : ''}
            </Typography>
          </Box>
        )}
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', height: 24, px: 1.25, borderRadius: 1, border: `1px solid ${rail}`, color: rail, bgcolor: alpha(rail, 0.06), fontSize: '0.72rem', fontWeight: 600, whiteSpace: 'nowrap' }}>
          New seller?
        </Box>
        {!split && (
          <Box sx={{ textAlign: 'right' }}>
            <Typography sx={LABEL_SX}>Mails</Typography>
            <Typography variant="body2" fontWeight={500} sx={{ fontVariantNumeric: 'tabular-nums' }}>
              {ref.mails}
            </Typography>
          </Box>
        )}
      </Paper>
    );
  };
  const showReferrals = tab === 'inbox' && (filter === 'all' || filter === 'you') && !search.identifier && !search.text && !partIdFilter && !supplierIdFilter;
  const list = tab === 'inbox' ? [...(showReferrals ? referrals.map(referralCard) : []), ...inboxRows(visible, filter).map(rowCard)] : groups.map(groupCard);

  return (
    <Box sx={{ width: '100%', pb: 4 }}>
      {/* One RFQ open full width is its own page: the breadcrumb and the panel's back link name the place. */}
      {!focused && (
        <Box sx={{ px: 3, pt: 1, mb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
            RFQ Center
          </Typography>
        </Box>
      )}

      {/* Gauge strip: click a gauge to filter every view. Hidden while one RFQ is open full width: that is its own page. */}
      <Box sx={{ position: 'relative', display: focused ? 'none' : 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 120, mb: 1, px: 3 }}>
        <Stack direction="row" spacing={2.5} sx={{ alignItems: 'flex-start', flexWrap: 'wrap', justifyContent: 'center', rowGap: 1 }}>
          {GAUGES.map((g) => (
            <RfqMetricGauge key={g.id} label={g.label} count={counts[g.id]} maxCount={counts.all || 1} color={g.color} Icon={g.Icon} active={filter === g.id} onClick={() => setParam({ f: g.id === 'all' ? null : g.id })} sublabel={g.sublabel} />
          ))}
        </Stack>
        <Box sx={{ position: 'absolute', right: 24, top: 24 }}>
          <Button size="small" onClick={() => void load()} startIcon={<RefreshIcon sx={{ fontSize: 16 }} />} sx={{ color: 'text.secondary' }}>
            Refresh
          </Button>
        </Box>
      </Box>

      <Box sx={{ px: 3 }}>
        <Tabs value={tab} onChange={(_, value: CenterTab) => setParam({ view: value === 'inbox' ? null : value, group: null, open: null })} sx={{ display: focused ? 'none' : 'flex', borderBottom: `1px solid ${theme.palette.divider}`, mb: 2, minHeight: 40 }}>
          {TABS.map((t) => (
            <Tab key={t} value={t} label={TAB_LABEL[t]} sx={{ textTransform: 'none', fontWeight: 600, minHeight: 40 }} />
          ))}
        </Tabs>

        <Box sx={{ display: focused ? 'none' : 'flex', justifyContent: 'center' }}>
          <Box sx={{ width: '100%', maxWidth: 760 }}>
            <UnifiedSearchBar value={search} onChange={setSearch} fetchBoms={fetchBoms} placeholders={{ identifier: 'RFQ #, part PN, MPN…', bom: 'All BOMs', text: 'Supplier, description…' }} />
          </Box>
        </Box>

        <Box sx={{ mt: focused ? 0 : 4, display: 'grid', gap: 2, alignItems: 'start', gridTemplateColumns: split ? { xs: '1fr', lg: 'minmax(300px, 380px) minmax(0, 1fr)' } : '1fr' }}>
          {!focused && (
            <Box sx={{ display: split ? { xs: 'none', lg: 'block' } : 'block', minWidth: 0 }}>
              {loading ? (
                <Stack spacing={1.25}>
                  {[1, 2, 3, 4].map((i) => (
                    <Skeleton key={i} variant="rounded" height={76} />
                  ))}
                </Stack>
              ) : list.length === 0 ? (
                <Paper elevation={0} sx={{ p: 6, textAlign: 'center', border: `1px dashed ${theme.palette.divider}`, borderRadius: 2 }}>
                  <Typography color="text.secondary" variant="body2">
                    {failed ? 'The RFQ Center could not be loaded.' : rows.length === 0 ? 'No RFQs yet. Create one from a Part or BOM page.' : 'Nothing matches the current filter or search.'}
                  </Typography>
                </Paper>
              ) : (
                <Stack spacing={1.25}>{list}</Stack>
              )}
            </Box>
          )}
          {arriving && !openRow && (
            <Box sx={{ bgcolor: 'background.paper', border: `1px solid ${theme.palette.divider}`, borderRadius: 2, px: 2.5, py: 2 }}>
              <Skeleton variant="text" width={180} height={32} />
              <Skeleton variant="text" width={260} />
              <Skeleton variant="rounded" height={320} sx={{ mt: 2 }} />
            </Box>
          )}
          {openReferral ? (
            <ReferralPanel
              referral={openReferral}
              onClose={() => setParam({ referral: null })}
              onDone={(rfqId) => {
                setParam({ referral: null, open: rfqId ?? null });
                void load();
              }}
            />
          ) : openRow ? (
            <ConversationPanel
              row={openRow}
              tab={tab}
              siblings={siblingsOf(openRow, visible.some((r) => r.rfqId === openRow.rfqId) ? visible : [...visible, openRow], tab, filter)}
              backLabel={openGroup?.title ?? (focused ? 'RFQ Center' : undefined)}
              expanded={expanded}
              onOpen={open}
              onBack={openGroup ? () => setParam({ group: openGroup.key, open: null }) : focused ? leaveFocus : undefined}
              onClose={() => {
                setExpanded(false);
                setParam({ open: null, group: null, rfqId: null, full: null });
              }}
              onToggleExpanded={() => setExpanded((v) => !v)}
              onChanged={() => void load()}
            />
          ) : openGroup && tab !== 'inbox' ? (
            <GroupPanel
              group={openGroup}
              tab={tab}
              onOpen={open}
              onClose={() => {
                setExpanded(false);
                setParam({ group: null });
              }}
            />
          ) : null}
        </Box>
      </Box>
    </Box>
  );
};

/** The old single-RFQ address: it now opens that RFQ's conversation in the RFQ Center. */
export const RfqPageRedirect: React.FC = () => {
  const { rfqId } = useParams<{ rfqId: string }>();
  return <Navigate to={`/rfq?open=${encodeURIComponent(rfqId ?? '')}&full=1`} replace />;
};

/** When the RFQ was created: the day ("16 Oct") and the time ("16:43"). */
function createdDay(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}
function createdTime(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '' : d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

export default RfqCenter;
